import { useMemo, type KeyboardEvent } from "react";
import { testIds } from "@/testIds";
import { changedLineCount, changedSentence, lineDiff, type LineHighlight, type PendingChange } from "@/lib/tikzAsk";
import styles from "./TikzAskDiff.module.scss";

const T = testIds.tikzAsk;

/**
 * Keep · Discard over the source while an Ask Lilia change is pending (1c):
 * "Ask Lilia changed 3 lines. It drew." Keep ⌘⏎ (solid) · Discard ⌘⌫.
 */
export function TikzDiffBar({ change, current, onKeep, onDiscard }: { change: PendingChange; current: string; onKeep: () => void; onDiscard: () => void }) {
  const n = useMemo(() => changedLineCount(lineDiff(current, change.source)), [current, change.source]);
  return (
    <div className={styles.bar} data-testid={T.diffBar} data-drew={change.drew ? "true" : "false"} role="status">
      <span className={styles.barText}>
        <b>{changedSentence(n)}</b>{" "}
        {change.drew
          ? "It drew. Review, then keep it or discard it."
          : `It didn't draw${change.error?.line != null ? ` (line ${change.error.line})` : ""}. Keep it to fix it here, or discard it.`}
      </span>
      <button type="button" className={styles.discard} data-testid={T.diffDiscard} title="Discard (⌘⌫)" onClick={onDiscard}>
        Discard
      </button>
      <button type="button" className={styles.keep} data-testid={T.diffKeep} title="Keep (⌘⏎)" onClick={onKeep}>
        Keep ⌘⏎
      </button>
    </div>
  );
}

/**
 * The source as a diff: removed lines (−) and added ones (+) among the kept
 * ones, numbered as the new source. Focusable: typing here keeps the change,
 * and the keystroke goes on into the source (`onType`).
 */
export function TikzDiffSource({
  current,
  change,
  highlight,
  onKeep,
  onDiscard,
  onType,
}: {
  current: string;
  change: PendingChange;
  highlight: LineHighlight | null;
  onKeep: () => void;
  onDiscard: () => void;
  /** A key that edits (a character, Enter, Backspace…): keep, then type it. */
  onType: (key: string) => void;
}) {
  const rows = useMemo(() => lineDiff(current, change.source), [current, change.source]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      onKeep();
      return;
    }
    if (mod && e.key === "Backspace") {
      e.preventDefault();
      e.stopPropagation();
      onDiscard();
      return;
    }
    if (mod || e.altKey) return;
    if (e.key.length === 1 || e.key === "Enter" || e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      onType(e.key);
    }
  };
  return (
    <div
      className={styles.diff}
      data-testid={T.diffSource}
      tabIndex={0}
      role="textbox"
      aria-readonly="false"
      aria-label="Ask Lilia's change to the source. Type to keep it and go on editing."
      onKeyDown={onKeyDown}
    >
      {rows.map((r, i) => {
        const lit = highlight && r.n !== null && r.n >= highlight.from && r.n <= highlight.to;
        return (
          <div
            key={i}
            className={styles.row}
            data-op={r.op === "-" ? "removed" : r.op === "+" ? "added" : "kept"}
            data-line={r.n ?? undefined}
            data-ai-highlight={lit ? "true" : undefined}
          >
            <span className={styles.num}>{r.n ?? ""}</span>
            <span className={styles.sign} aria-hidden="true">
              {r.op === "-" ? "−" : r.op === "+" ? "+" : ""}
            </span>
            <span className={styles.text}>{r.text || " "}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Before · After on the drawing while a change is pending. */
export function TikzBeforeAfter({ after, onChange }: { after: boolean; onChange: (after: boolean) => void }) {
  return (
    <div className={styles.toggle} data-testid={T.diffBeforeAfter} role="group" aria-label="Before or after Ask Lilia's change" data-showing={after ? "after" : "before"}>
      <button type="button" aria-pressed={!after} data-testid={T.diffBefore} onClick={() => onChange(false)}>
        Before
      </button>
      <button type="button" aria-pressed={after} data-testid={T.diffAfter} onClick={() => onChange(true)}>
        After
      </button>
    </div>
  );
}
